import {useCallback} from "react";
import {useNavigate, NavigateOptions, To} from "react-router-dom";
import {useLocalization} from "@fluent/react";
import {FluentVariable} from "@fluent/bundle";
import {SUPPORTED_LANGUAGES, SupportedLanguage} from "./constants";
import {useLanguage} from "i18n/LanguageProvider";

export const withLanguagePrefix = (lang: SupportedLanguage, path: string): string => {
    if (!path.startsWith("/")) {
        return path;
    }

    const trimmed = path.replace(/^\/+/, "");

    if (trimmed.length === 0) {
        return `/${lang}`;
    }

    const alreadyPrefixed = SUPPORTED_LANGUAGES.some((item) => trimmed === item || trimmed.startsWith(`${item}/`));

    if (alreadyPrefixed) {
        return `/${trimmed}`;
    }

    return `/${lang}/${trimmed}`;
};

const localizeTo = (lang: SupportedLanguage, to: To): To => {
    if (typeof to === "string") {
        return withLanguagePrefix(lang, to);
    }

    if (typeof to === "number") {
        return to;
    }

    return {
        ...to,
        pathname: to.pathname ? withLanguagePrefix(lang, to.pathname) : to.pathname,
    };
};

export const useLocalizedNavigate = () => {
    const navigate = useNavigate();
    const {lang} = useLanguage();

    return useCallback(
        (to: To | number, options?: NavigateOptions) => {
            if (typeof to === "number") {
                navigate(to);
                return;
            }

            navigate(localizeTo(lang, to), options);
        },
        [lang, navigate],
    );
};

export const useLocalizedPath = () => {
    const {lang} = useLanguage();

    return useCallback((path: string) => withLanguagePrefix(lang, path), [lang]);
};

export const useL10n = () => {
    const {l10n} = useLocalization();

    const translate = useCallback(
        (id: string, args?: Record<string, FluentVariable>, fallback?: string) => {
            try {
                return l10n.getString(id, args, fallback ?? id);
            } catch (error) {
                console.error(`Missing translation for key: ${id}`, error);
                return fallback ?? id;
            }
        },
        [l10n],
    );

    return {t: translate};
};
